// 参照モードのマップ一括編集セッション用ユーティリティ（純関数）。
//
// 移動・回転などの編集はルートごとの「編集後マーク列」として蓄積し、
// 機体側 (mark_trajectory_generator) と同じ規約でステーション / スロットを
// 再生成した「編集後マップ」を合成して表示・次の編集の基準に使う。
// 機体への保存（再記録）まではルート ID を変えない。

import type { EditableMark, RouteMap, Slot, Station } from '@/types'
import { SLOT_TYPE_CARGO } from '@/config/scenarioConfig'
import { computeMarkThetas } from './markUtil'

// マーク列からステーション / スロットを機体側と同じ規約で再生成する。
// - マーク i と i+1 の間の両ステーション rec-{i}-{i+1}（マーク i 位置）/
//   rec-{i+1}-{i}（マーク i+1 位置）の theta は、どちらも区間の記録時ヘディング
//   （経路方向、後進区間は +π）= computeMarkThetas[i+1]。逆方向走行時は
//   ギアを反転してヘディングは変えないため +π しない（爪方向アニメーションの
//   「後進では爪が進行方向と逆を向く」表示もこの規約に依存する）。
// - スロット rec-1（先頭マーク）/ rec-2（末尾マーク）に height / type を保存
export function buildRouteMapEntries(
  mapId: string,
  marks: EditableMark[]
): { stations: Station[]; slots: Slot[] } {
  const thetas = computeMarkThetas(marks)
  const stations: Station[] = []
  for (let i = 0; i + 1 < marks.length; i++) {
    stations.push({
      id: `*r${mapId}/rec-${i}-${i + 1}`,
      pose: { x: marks[i].x, y: marks[i].y, theta: thetas[i + 1] },
    })
    stations.push({
      id: `*r${mapId}/rec-${i + 1}-${i}`,
      pose: { x: marks[i + 1].x, y: marks[i + 1].y, theta: thetas[i + 1] },
    })
  }
  const slotOf = (
    name: string,
    stationId: string,
    mark: EditableMark
  ): Slot => ({
    id: `*r${mapId}/${name}`,
    stationId,
    height: mark.isCargo ? Math.max(mark.height, 0) : -1,
    type: mark.isCargo ? SLOT_TYPE_CARGO : 0,
  })
  const last = marks.length - 1
  const slots: Slot[] = [
    slotOf('rec-1', `*r${mapId}/rec-0-1`, marks[0]),
    slotOf('rec-2', `*r${mapId}/rec-${last}-${last - 1}`, marks[last]),
  ]
  return { stations, slots }
}

// 編集後マーク列（mapId → marks）を元マップへ合成した「編集後マップ」を返す。
// 編集の無いルートはそのまま。編集済みルートのガイドラインは（表示に未使用の
// ため）削除のみ行う。edits が空なら元マップをそのまま返す。
export function applyRouteEditsToMap(
  map: RouteMap,
  edits: Map<string, EditableMark[]>
): RouteMap {
  if (edits.size === 0) return map
  const stations = new Map(map.stations)
  const slots = new Map(map.slots)
  const guidelines = new Map(map.guidelines)
  for (const [mapId, marks] of edits) {
    if (marks.length < 2) continue
    const prefix = `*r${mapId}/`
    for (const id of [...stations.keys()]) {
      if (id.startsWith(prefix)) stations.delete(id)
    }
    for (const id of [...slots.keys()]) {
      if (id.startsWith(prefix)) slots.delete(id)
    }
    for (const key of [...guidelines.keys()]) {
      if (key.includes(prefix)) guidelines.delete(key)
    }
    const entries = buildRouteMapEntries(mapId, marks)
    for (const st of entries.stations) stations.set(st.id, st)
    for (const slot of entries.slots) slots.set(slot.id, slot)
  }
  return { stations, slots, guidelines }
}
